// We use imported types only for the static helpers below
import {
  ResultFund,
  ResultDownload,
  Filter,
  Facet,
  DownloadItem,
} from '../components/ui-download-search/store';
import { filterParams } from '../components/ui-download-search/ui-download-search-quickfilters/filters';

export interface IntergatorConnectorType {
  url: string;
  key: string;
  cache?: boolean;
}

export interface IntergatorResultsQuery {
  q: string;
  filters: string[];
  of: number; // offset
  si: number; // results per page
  od: string; // sorting
}

export interface IntergatorFacetsQuery {
  q: string;
  filters: string[];
}

export interface IntergatorSuggestionsQuery {
  keyword: string;
  filters: string[];
}

export class IntergatorConnector {
  requestHeaders: any;

  requestUrl: URL;

  cacheUsed = false;

  cachedResponses = {};

  constructor({ url, key, cache }: IntergatorConnectorType) {
    this.requestHeaders = {
      headers: {
        'x-api-key': key,
      },
    };
    this.requestUrl = new URL(url);
    this.cacheUsed = cache;
  }

  private getUrlWithParams(params: any) {
    const urlWithParams = new URL(this.requestUrl.href);
    Object.entries(params).forEach(([key, value]) => {
      if (key !== 'filters') {
        urlWithParams.searchParams.append(key, value.toString());
      } else {
        (value as Array<any>).forEach((filter) => {
          urlWithParams.searchParams.append('filter', filter);
        });
      }
    });
    return urlWithParams.href;
  }

  private async queriedFetch(params: any) {
    const url = this.getUrlWithParams(params);
    if (this.cacheUsed && this.cachedResponses[url]) {
      return this.cachedResponses[url];
    }
    const response = fetch(url, this.requestHeaders);
    return response
      .then(
        () =>
          this.cacheUsed &&
          Object.assign(this.cachedResponses, { [url]: response })
      )
      .then(() => response);
  }

  public async fetchSuggestions(query: IntergatorSuggestionsQuery) {
    const response = this.queriedFetch({
      keyword: `${query.keyword}`,
      filters: query.filters,
    });
    return response.then(() => response);
  }

  public async fetchFacets(query: IntergatorFacetsQuery) {
    const sortedQuery = {
      q: query.q || '*',
      actualQ: query.q || '*',
      filters: query.filters.sort(),
    };
    const response = this.queriedFetch(sortedQuery);
    return response.then(() => response);
  }

  public async fetchResults(query: IntergatorResultsQuery) {
    const sortedQuery = {
      q: query.q || '*',
      actualQ: query.q || '*',
      filters: query.filters.sort(),
      of: query.of,
      si: query.si,
      od: query.od,
    };
    const response = this.queriedFetch(sortedQuery);
    return response.then(() => response);
  }

  // Helpers especially for Download Search

  static convertFacets(results) {
    return results[0].filter
      .filter((facet) =>
        ['facet_celum_assetKind', 'facet_date'].includes(facet.name)
      )
      .map(
        (facet): Facet => ({
          name: facet.label.replace('Assetart', 'Dokumententyp'),
          value: facet.name,
          type: 'checkbox',
          filters: facet.categories.map(
            (filter): Filter => ({
              name: filter.label,
              value: filter.name,
              results: filter.searchMdc,
            })
          ),
        })
      );
  }

  static convertQForDownloadSearch(q) {
    return q
      ?.replace(/fonds:/g, 'fondsname:')
      .replace(/topic:/g, 'celum.topicSelection:')
      .replace(/type:/g, 'celum.materialSelection:');
  }

  static convertFundsQForDownloadSearch(funds: string[]) {
    return funds.map((fund) => `isin:${fund}`).join(' OR ');
  }

  /**
   * Convert Intergator result data to `ResultDownload` objects
   *
   * @param results Intergator result data
   */
  static convertDownloads(results): ResultDownload[] {
    return results.hits.map(
      (result): ResultDownload => ({
        title: result.title,
        image: result.thumbnail,
        isin: result.isin,
        id: result.documentId,
        date: result.date,
        description: result.content.replace('<p>', '').replace('</p>', ''),
        url: result.url,
        filetype: result.filetype,
        filesize: Math.round(result.filesize / 1024),
      })
    );
  }

  /**
   * Convert Intergator result data to `ResultFund` objects
   *
   * @param results Intergator result data
   */
  static convertFunds(results): ResultFund[] {
    return results.hits
      .filter((result) => !result.filetype)
      .map(
        (result): ResultFund =>
          ({
            id: result.documentId,
            title: result.title,
            image: result.thumbnail,
            isin: result.isin,
            wkn: result.wkn,
            downloads: this.getFundsDownloads(result),
          }) as ResultFund
      );
  }

  /**
   * Generate downloads from verkaufsunterlagen and sustainibilityReport
   *
   * @param results Intergator result data
   */
  static getFundsDownloads(result): DownloadItem[] {
    const downloads = [
      result.verkaufsunterlagen,
      result.sustainabilityReport ? [result.sustainabilityReport] : [],
    ];
    return downloads
      .filter((section) => section)
      .flatMap((section) =>
        section.map((download) => ({
          title: download.title,
          url: download.url,
          id: download.documentId,
          filesize: Math.round(download.filesize / 1024),
          type: download.type,
          path: download.path,
        }))
      );
  }

  static convertSuggestionGroups(results) {
    let i = 0;
    return results.map((result) => ({
      ...result,
      hits: result.hits.map((suggestion) => {
        i += 1;
        return {
          ...suggestion,
          id: i - 1,
        };
      }),
    }));
  }

  static getFacetFromFilter(filter) {
    return filter.value.split(':')[0];
  }

  static getDownloadSearchFilters(fundsMode: boolean, filters: string[]) {
    return fundsMode
      ? [...filters, 'facet_exclude_no_vku:true']
      : [filterParams.downloads, ...filters];
  }

  static getDownloadSearchSuggestionFilters(fundsMode: boolean) {
    return fundsMode ? [filterParams.pkfonds] : [];
  }

  /**
   * Returning equal number of results per group, so the final number of results in all groups
   * together is not greater then given limit. In case on group has less results then limited,
   * some other group can increase number of returned results for that number.
   * Example:
   * suggestionGroups: {[4 results],[10 results]}; limit :12
   * Limited number of results per group would be 12/2 = 6 results per group
   * first group will return 4  (2 stays as availableSpots)
   * second group will return 6 + 2 availableSpots = 8
   */
  static returnLimitedNumberOfSuggestions(suggestionGroups, limit: number) {
    const numberOfSuggestionGroups = suggestionGroups.length;
    const maxNumberOfSuggestionsPerGroup = Math.floor(
      limit / numberOfSuggestionGroups
    );
    let availableSpots = 0;

    // Firstly we collect the sum of available spots per group, difference if limit > results
    // this comes first, in case first suggestionGroup can use availableSpots
    suggestionGroups?.map((group) => {
      if (group.hits.length < maxNumberOfSuggestionsPerGroup) {
        availableSpots += maxNumberOfSuggestionsPerGroup - group.hits.length;
      }
      return availableSpots;
    });

    suggestionGroups?.map((group, index) => {
      const totalResultsInGroup = group.hits.length;
      // new limit for results in current group
      let newLimit;

      // Adjustment of results per group just in case group has more results then limit allows
      if (totalResultsInGroup > maxNumberOfSuggestionsPerGroup) {
        // new limit is increased
        const maxNumberOfSuggestionsInThisGroup =
          maxNumberOfSuggestionsPerGroup + availableSpots;

        // Case 1 : number of results in this group can not take all available spots
        // Case 2 : no available spots will remain after this group
        if (totalResultsInGroup <= maxNumberOfSuggestionsInThisGroup) {
          availableSpots =
            maxNumberOfSuggestionsInThisGroup - totalResultsInGroup;
          newLimit = totalResultsInGroup;
        } else {
          availableSpots = 0;
          newLimit = maxNumberOfSuggestionsInThisGroup;
        }
      }
      const limitedResults = group.hits.slice(0, newLimit);
      // eslint-disable-next-line no-param-reassign
      suggestionGroups[index].hits = limitedResults;
      return suggestionGroups[index];
    });
    return suggestionGroups;
  }
}
